import { useEffect, useRef, useState, useCallback } from 'react';

export default function Lightbox({ images = [], currentIndex = 0, isOpen = false, onClose }) {
  const [index, setIndex] = useState(currentIndex);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    setIndex(currentIndex);
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (!images.length) return;
    setIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const handlePrev = useCallback(() => {
    if (!images.length) return;
    setIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose, handleNext, handlePrev]);

  if (!isOpen || !images.length) return null;

  const currentImage = images[index];
  const src = typeof currentImage === 'string' ? currentImage : currentImage.url || currentImage.src;
  const caption = typeof currentImage === 'object' ? currentImage.alt || currentImage.caption || '' : '';

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    if (touchStartX.current - touchEndX.current > 50) {
      handleNext();
    }
    if (touchEndX.current - touchStartX.current > 50) {
      handlePrev();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md transition-opacity animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Top bar controls */}
      <div
        className="absolute top-0 inset-x-0 p-4 sm:p-6 flex items-center justify-between z-10 bg-gradient-to-b from-black/80 to-transparent"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs sm:text-sm font-open-sans tracking-wider text-slate-300">
          <span className="text-cyan-400 font-semibold">{index + 1}</span> / {images.length}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2.5 rounded-lg bg-slate-900/80 border border-slate-700/60 text-slate-300 hover:text-white hover:border-cyan-500/50 hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
          aria-label="Luk billedvisning (Esc)"
        >
          <i className="fa-solid fa-xmark text-lg w-5 h-5 flex items-center justify-center" aria-hidden="true" />
        </button>
      </div>

      {/* Main image container */}
      <div
        className="relative max-w-7xl max-h-[85vh] w-full mx-4 flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={src}
          alt={caption || `Billede ${index + 1}`}
          className="max-h-[75vh] w-auto max-w-full object-contain rounded-md shadow-2xl border border-slate-800"
        />
        {caption && (
          <p className="mt-3 text-sm text-center text-slate-300 font-sans max-w-2xl px-4 py-1.5 rounded bg-slate-900/80 border border-slate-800">
            {caption}
          </p>
        )}
      </div>

      {/* Navigation buttons */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-3 sm:p-4 rounded-full bg-slate-900/80 border border-slate-700/60 text-slate-200 hover:text-cyan-400 hover:border-cyan-500/60 hover:bg-slate-800 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-base sm:text-xl" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-3 sm:p-4 rounded-full bg-slate-900/80 border border-slate-700/60 text-slate-200 hover:text-cyan-400 hover:border-cyan-500/60 hover:bg-slate-800 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-base sm:text-xl" aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
}
